import { Link, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { Languages, LogOut } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { ThemeSwitch } from '@/components/theme/ThemeSwitch';

/**
 * Persistent shell for authenticated routes. Mirrors the corefi-bank
 * top-bar pattern: brand on the left, primary nav in the middle, account
 * actions on the right.
 */
export function AppShell() {
  const auth = useAuth();
  const location = useLocation();

  const navItems = [
    { to: '/workspaces', label: 'Workspaces' },
    { to: '/docs', label: 'Docs' },
  ];

  return (
    <div className="flex h-full flex-col bg-background text-foreground">
      <header className="border-b bg-surface">
        <div className="mx-auto flex h-14 max-w-7xl items-center gap-6 px-4">
          <Link
            to="/workspaces"
            className="flex items-center gap-2 text-sm font-semibold tracking-tight"
          >
            <Languages className="size-5 text-primary" aria-hidden />
            <span>Fluentix</span>
          </Link>
          <nav className="flex flex-1 items-center gap-1" aria-label="Primary">
            {navItems.map((item) => {
              const active = location.pathname.startsWith(item.to);
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  className={cn(
                    'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                    active
                      ? 'bg-primary/10 text-primary'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                  )}
                >
                  {item.label}
                </Link>
              );
            })}
          </nav>
          <div className="flex items-center gap-3">
            <ThemeSwitch compact />
            <span className="hidden text-xs text-muted-foreground sm:inline">
              {auth.user?.profile.email ?? auth.user?.profile.preferred_username ?? ''}
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => void auth.signoutRedirect()}
            >
              <LogOut aria-hidden />
              Sign out
            </Button>
          </div>
        </div>
      </header>
      <main className="mx-auto w-full max-w-7xl flex-1 px-4 py-6">
        <Outlet />
      </main>
    </div>
  );
}
